import React from 'react';
import { User, Order, OrderStatus, UserRole } from '../types';
import { Card, StatusBadge } from './ui';
import {
    Activity, CheckCircle, Wallet, Plus, ArrowRight, CreditCard, FileText
} from 'lucide-react';

interface DentistDashboardProps {
    user: User;
    orders: Order[];
    onNavigate: (path: string) => void;
    onNewCase: () => void;
    onTopUp: () => void;
}

export const DentistDashboard: React.FC<DentistDashboardProps> = ({
    user,
    orders,
    onNavigate,
    onNewCase,
    onTopUp
}) => {
    // Filter orders for dentist
    const myOrders = orders.filter(o => o.doctorId === user.id || (user.role === UserRole.MODERATOR && o.doctorId === user.parentId));

    // Stats
    const active = myOrders.filter(o => o.status !== OrderStatus.DELIVERED && o.status !== OrderStatus.CANCELLED).length;
    const completed = myOrders.filter(o => o.status === OrderStatus.DELIVERED).length;

    // Recent Orders (Last 5)
    const recentOrders = myOrders.slice(0, 5);

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Welfare / Welcome Section */}
            <div className="flex flex-col md:flex-row justify-between items-end gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-primary">Welcome, {user.name}</h1>
                    <p className="text-secondary mt-1">Here is what's happening with your cases today.</p>
                </div>

                {/* Primary Action */}
                <button
                    onClick={onNewCase}
                    className="bg-brand hover:brightness-110 text-white font-bold rounded-xl px-8 py-4 shadow-lg shadow-brand/30 flex items-center gap-3 transition-transform hover:scale-105 active:scale-95 group"
                >
                    <div className="bg-white/20 p-2 rounded-lg group-hover:rotate-90 transition-transform duration-300">
                        <Plus size={24} />
                    </div>
                    <div className="text-left">
                        <div className="text-base leading-none">Create New Case</div>
                        <div className="text-xs opacity-90 font-normal mt-1">Start a new order</div>
                    </div>
                </button>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-surface shadow-card border border-border hover:border-brand/50 transition-all group relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                        <Activity size={100} className="text-brand transform rotate-12" />
                    </div>
                    <div className="flex justify-between items-start relative z-10">
                        <div>
                            <p className="text-secondary font-medium mb-1">Active Cases</p>
                            <h3 className="text-4xl font-bold text-primary">{active}</h3>
                        </div>
                        <div className="p-3 bg-brand/10 rounded-xl text-brand">
                            <Activity size={24} />
                        </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
                        <span className="text-secondary">In progress</span>
                        <button onClick={() => onNavigate('#/dashboard/orders')} className="text-brand font-medium hover:underline flex items-center gap-1">
                            View All <ArrowRight size={14} />
                        </button>
                    </div>
                </Card>

                <Card className="bg-surface shadow-card border border-border hover:border-emerald-500/50 transition-all group relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                        <CheckCircle size={100} className="text-emerald-500 transform rotate-12" />
                    </div>
                    <div className="flex justify-between items-start relative z-10">
                        <div>
                            <p className="text-secondary font-medium mb-1">Completed</p>
                            <h3 className="text-4xl font-bold text-primary">{completed}</h3>
                        </div>
                        <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-500">
                            <CheckCircle size={24} />
                        </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
                        <span className="text-secondary">Delivered</span>
                        <span className="text-emerald-500 font-medium">Lifetime</span>
                    </div>
                </Card>

                <Card className="bg-surface shadow-card border border-border hover:border-amber-500/50 transition-all group relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                        <Wallet size={100} className="text-amber-500 transform rotate-12" />
                    </div>
                    <div className="flex justify-between items-start relative z-10">
                        <div>
                            <p className="text-secondary font-medium mb-1">Wallet Balance</p>
                            <h3 className={`text-4xl font-bold ${(user.balance || 0) < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                                {user.balance.toLocaleString()}
                                <span className="text-xl text-secondary ml-1 font-normal">EGP</span>
                            </h3>
                        </div>
                        <div className="p-3 bg-amber-500/10 rounded-xl text-amber-500">
                            <Wallet size={24} />
                        </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
                        <button onClick={onTopUp} className="text-amber-600 dark:text-amber-400 font-bold hover:underline flex items-center gap-1">
                            <Plus size={14} /> Top Up Balance
                        </button>
                        <button onClick={() => onNavigate('#/dashboard/invoices')} className="text-secondary hover:text-primary flex items-center gap-1">
                            History
                        </button>
                    </div>
                </Card>
            </div>

            {/* Recent Orders & Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Recent Activity Table */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="flex justify-between items-center px-1">
                        <h2 className="text-xl font-bold text-primary flex items-center gap-2">
                            <Activity size={20} className="text-brand" />
                            Recent Activity
                        </h2>
                        <button onClick={() => onNavigate('#/dashboard/orders')} className="text-sm text-brand hover:underline font-medium">View All Orders</button>
                    </div>

                    <div className="bg-surface border border-border rounded-xl overflow-hidden shadow-card">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-surfaceHighlight text-secondary text-xs font-bold uppercase">
                                    <tr>
                                        <th className="p-4 rounded-tl-xl">Patient</th>
                                        <th className="p-4 hidden sm:table-cell">Type</th>
                                        <th className="p-4">Status</th>
                                        <th className="p-4 text-right rounded-tr-xl hidden sm:table-cell">Due Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {recentOrders.map(order => (
                                        <tr key={order.id} className="hover:bg-surfaceHighlight/50 cursor-pointer transition-colors group">
                                            <td className="p-4 font-bold text-primary group-hover:text-brand transition-colors">
                                                {order.patientName}
                                                <div className="sm:hidden text-xs text-secondary mt-1 font-normal">{order.items[0]?.serviceName || 'Custom'}</div>
                                            </td>
                                            <td className="p-4 text-secondary hidden sm:table-cell">{order.items[0]?.serviceName || 'Custom'}</td>
                                            <td className="p-4"><StatusBadge status={order.status} /></td>
                                            <td className="p-4 text-right text-secondary font-mono hidden sm:table-cell">{new Date(order.dueDate).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                    {recentOrders.length === 0 && (
                                        <tr><td colSpan={4} className="p-8 text-center text-secondary">No recent activity.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Quick Actions Panel */}
                <div className="space-y-4">
                    <h2 className="text-xl font-bold text-primary px-1">Quick Actions</h2>
                    <div className="grid grid-cols-1 gap-4">
                        <button onClick={() => onNavigate('#/dashboard/price_list')} className="bg-surface hover:bg-surfaceHighlight border border-border p-4 rounded-xl flex items-center gap-4 transition-all group hover:border-brand/30 shadow-card">
                            <div className="p-3 bg-purple-500/10 rounded-lg text-purple-500 group-hover:scale-110 transition-transform">
                                <FileText size={24} />
                            </div>
                            <div className="text-left">
                                <div className="font-bold text-primary">Price List</div>
                                <div className="text-xs text-secondary">View services & prices</div>
                            </div>
                            <ArrowRight size={16} className="ml-auto text-secondary group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </button>

                        <button onClick={() => onNavigate('#/dashboard/invoices')} className="bg-surface hover:bg-surfaceHighlight border border-border p-4 rounded-xl flex items-center gap-4 transition-all group hover:border-brand/30 shadow-card">
                            <div className="p-3 bg-blue-500/10 rounded-lg text-blue-500 group-hover:scale-110 transition-transform">
                                <CreditCard size={24} />
                            </div>
                            <div className="text-left">
                                <div className="font-bold text-primary">Invoices</div>
                                <div className="text-xs text-secondary">Check payments</div>
                            </div>
                            <ArrowRight size={16} className="ml-auto text-secondary group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </button>

                        <button onClick={onTopUp} className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 hover:from-amber-500/20 hover:to-orange-500/20 border border-amber-500/20 p-4 rounded-xl flex items-center gap-4 transition-all group shadow-card">
                            <div className="p-3 bg-amber-500/20 rounded-lg text-amber-600 dark:text-amber-500 group-hover:scale-110 transition-transform">
                                <Wallet size={24} />
                            </div>
                            <div className="text-left">
                                <div className="font-bold text-primary">Top Up Wallet</div>
                                <div className="text-xs text-secondary">Add funds securely</div>
                            </div>
                            <Plus size={16} className="ml-auto text-amber-600 dark:text-amber-500 group-hover:rotate-90 transition-transform" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
